import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { Loader2, Building2, Palette, FileText, Users, Plus, Trash2, MessageCircle, Upload, Mail, Lock } from "lucide-react";
import api from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { MailSettings } from "@/components/mail/MailSettings";
import { WhatsAppConnect } from "@/components/whatsapp/WhatsAppConnect";
import { UpgradeCard } from "@/components/company/UpgradeCard";
import { BackupCard } from "@/components/company/BackupCard";
import { LanguageSetting } from "@/components/LanguageSetting";
import { useAuth } from "@/context/AuthContext";
import { isSimple } from "@/lib/edition";

const COLORS = ["#E1352A", "#EA580C", "#2563EB", "#059669", "#DC2626", "#7C3AED", "#0891B2", "#111827"];

const Section = ({ icon: Icon, title, desc, children }) => (
  <div className="rounded-xl border border-border bg-card p-6 space-y-5">
    <div className="flex items-center gap-3">
      <div className="w-10 h-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center"><Icon className="w-5 h-5" /></div>
      <div>
        <h3 className="font-heading font-semibold">{title}</h3>
        <p className="text-xs text-muted-foreground">{desc}</p>
      </div>
    </div>
    {children}
  </div>
);

export default function Impostazioni() {
  const { user } = useAuth();
  const simple = isSimple(user);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const logoRef = useRef(null);
  const [docCode, setDocCode] = useState("");
  const [hasDocCode, setHasDocCode] = useState(false);
  const [savingCode, setSavingCode] = useState(false);

  useEffect(() => {
    api.get("/settings").then((r) => setForm(r.data));
    api.get("/settings/documents-code").then((r) => setHasDocCode(r.data.has_code)).catch(() => {});
  }, []);

  if (!form) return <div className="text-muted-foreground">Caricamento…</div>;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const collaboratori = form.collaboratori || [];
  const setColl = (arr) => setForm((f) => ({ ...f, collaboratori: arr }));
  const addColl = () => setColl([...collaboratori, { nome: "", telefono: "" }]);
  const updColl = (i, k, v) => setColl(collaboratori.map((c, idx) => (idx === i ? { ...c, [k]: v } : c)));
  const delColl = (i) => setColl(collaboratori.filter((_, idx) => idx !== i));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings", { ...form, iva_default: Number(form.iva_default) || 22 });
      toast.success("Impostazioni salvate");
      window.dispatchEvent(new Event("settings-updated"));
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingLogo(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/settings/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, logo_url: r.data.logo_url }));
      toast.success("Logo caricato. Ricordati di salvare le impostazioni.");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setUploadingLogo(false); if (logoRef.current) logoRef.current.value = ""; }
  };

  const saveDocCode = async () => {
    if (docCode.length < 4) { toast.error("Il codice deve avere almeno 4 caratteri"); return; }
    setSavingCode(true);
    try {
      await api.put("/settings/documents-code", { code: docCode });
      setHasDocCode(true); setDocCode("");
      toast.success("Codice documenti salvato");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSavingCode(false); }
  };

  return (
    <div className="max-w-3xl space-y-6">
      {simple && <UpgradeCard />}
      <LanguageSetting />
      <Section icon={Building2} title="Profilo Azienda" desc={simple ? "I dati della tua ditta." : "Questi dati compaiono nell'intestazione del preventivo PDF."}>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-2 sm:col-span-2"><Label>Ragione Sociale</Label><Input value={form.ragione_sociale} onChange={set("ragione_sociale")} data-testid="settings-company-input" /></div>
          <div className="space-y-2"><Label>P.IVA</Label><Input value={form.piva} onChange={set("piva")} data-testid="settings-piva-input" /></div>
          <div className="space-y-2"><Label>Codice fiscale</Label><Input value={form.codice_fiscale || ""} onChange={set("codice_fiscale")} data-testid="settings-cf-input" /></div>
          <div className="space-y-2"><Label>PEC</Label><Input value={form.pec || ""} onChange={set("pec")} data-testid="settings-pec-input" /></div>
          <div className="space-y-2"><Label>Telefono</Label><Input value={form.telefono} onChange={set("telefono")} /></div>
          <div className="space-y-2"><Label>Email</Label><Input value={form.email} onChange={set("email")} /></div>
          <div className="space-y-2"><Label>IBAN</Label><Input value={form.iban} onChange={set("iban")} /></div>
          <div className="space-y-2 sm:col-span-2"><Label>Indirizzo</Label><Input value={form.indirizzo} onChange={set("indirizzo")} /></div>
          <div className="space-y-2 sm:col-span-2"><Label>Sito web</Label><Input value={form.sito || ""} onChange={set("sito")} placeholder="www.tuaditta.it" data-testid="settings-website-input" /></div>
          <div className="space-y-2 sm:col-span-2">
            <Label>Logo aziendale</Label>
            <div className="flex items-center gap-4">
              {form.logo_url ? (
                <img src={form.logo_url} alt="Logo" className="h-14 w-auto max-w-[160px] object-contain rounded-md border border-border bg-white p-1" data-testid="logo-preview" />
              ) : (
                <div className="h-14 w-24 rounded-md border border-dashed border-border flex items-center justify-center text-xs text-muted-foreground">Nessun logo</div>
              )}
              <input ref={logoRef} type="file" accept=".png,.jpg,.jpeg,.webp" onChange={uploadLogo} className="hidden" data-testid="logo-file-input" />
              <Button type="button" variant="outline" onClick={() => logoRef.current?.click()} disabled={uploadingLogo} data-testid="logo-upload-button">
                {uploadingLogo ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Upload className="w-4 h-4 mr-1.5" />} Carica logo
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">PNG, JPG o WEBP (max 3 MB). {simple ? "Compare nel menu dell'app." : "Compare nell'intestazione del preventivo PDF."}</p>
          </div>
          <div className="space-y-2 sm:col-span-2"><Label>Oppure incolla un URL logo</Label><Input value={form.logo_url} onChange={set("logo_url")} placeholder="https://…" data-testid="logo-url-input" /></div>
        </div>
      </Section>

      {!simple && (
      <Section icon={Mail} title="Promemoria Email" desc="Attiva il pulsante per inviarti un'email di promemoria delle scadenze di preventivi e fatture.">
        <div className="flex items-center justify-between gap-4">
          <div className="text-sm text-muted-foreground">Mostra il pulsante «Invia promemoria email» su preventivi e fatture.</div>
          <Switch checked={!!form.email_reminders_enabled} onCheckedChange={(v) => setForm((f) => ({ ...f, email_reminders_enabled: v }))} data-testid="email-reminders-toggle" />
        </div>
      </Section>
      )}

      <MailSettings />
      <WhatsAppConnect />

      <Section icon={Lock} title="Documenti clienti (GDPR)" desc="Codice per sbloccare i documenti riservati dei clienti (visibili solo all'amministratore, dal dettaglio contatto).">
        <div className="space-y-2">
          <div className="text-sm text-muted-foreground">{hasDocCode ? "Un codice è già impostato. Inseriscine uno nuovo per cambiarlo." : "Nessun codice impostato: impostane uno per abilitare i documenti cliente."}</div>
          <div className="flex gap-2 flex-wrap">
            <Input type="password" value={docCode} onChange={(e) => setDocCode(e.target.value)} placeholder="Nuovo codice (min 4 caratteri)" className="sm:w-64" data-testid="documents-code-input" />
            <Button variant="outline" onClick={saveDocCode} disabled={savingCode} data-testid="documents-code-save">{savingCode ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : null} {hasDocCode ? "Cambia codice" : "Imposta codice"}</Button>
          </div>
        </div>
      </Section>

      {!simple && (
      <>
      <Section icon={Palette} title="Brand & Colore" desc="Personalizza il colore d'accento del preventivo e lo slogan.">
        <div className="space-y-3">
          <Label>Colore accento</Label>
          <div className="flex items-center gap-2 flex-wrap">
            {COLORS.map((c) => (
              <button key={c} onClick={() => setForm((f) => ({ ...f, accent_color: c }))} className={`w-9 h-9 rounded-lg transition-transform hover:scale-110 ${form.accent_color === c ? "ring-2 ring-offset-2 ring-offset-card ring-foreground" : ""}`} style={{ background: c }} data-testid={`color-${c}`} />
            ))}
            <Input type="color" value={form.accent_color} onChange={set("accent_color")} className="w-14 h-9 p-1" />
          </div>
        </div>
        <div className="space-y-2"><Label>Slogan (in fondo al preventivo)</Label><Input value={form.slogan} onChange={set("slogan")} /></div>
      </Section>

      <Section icon={FileText} title="Preventivi — Valori predefiniti" desc="Impostazioni applicate ai nuovi preventivi.">
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-2"><Label>IVA predefinita %</Label><Input type="number" value={form.iva_default} onChange={set("iva_default")} /></div>
          <div className="space-y-2"><Label>Termini di pagamento</Label><Input value={form.termini_default} onChange={set("termini_default")} /></div>
          <div className="space-y-2 sm:col-span-2"><Label>Nota predefinita</Label><Textarea value={form.nota_default} onChange={set("nota_default")} rows={3} /></div>
          <div className="space-y-2 sm:col-span-2"><Label>Garanzia predefinita</Label><Textarea value={form.garanzia_default || ""} onChange={set("garanzia_default")} rows={3} data-testid="settings-garanzia-input" /></div>
        </div>
      </Section>
      </>
      )}

      <Section icon={Users} title="Collaboratori (WhatsApp)" desc={`I numeri a cui condividere ${simple ? "gli appuntamenti" : "i sopralluoghi"} dall'Agenda tramite WhatsApp.`}>
        <div className="space-y-3">
          {collaboratori.length === 0 && <p className="text-sm text-muted-foreground">Nessun collaboratore. Aggiungine uno per condividere {simple ? "gli appuntamenti" : "i sopralluoghi"} su WhatsApp.</p>}
          {collaboratori.map((c, i) => (
            <div key={i} className="flex gap-2 items-center" data-testid={`collaboratore-row-${i}`}>
              <Input value={c.nome} onChange={(e) => updColl(i, "nome", e.target.value)} placeholder="Nome" className="flex-1" data-testid={`collaboratore-nome-${i}`} />
              <Input value={c.telefono} onChange={(e) => updColl(i, "telefono", e.target.value)} placeholder="Numero WhatsApp (es. 3714331327)" className="flex-1" data-testid={`collaboratore-tel-${i}`} />
              <Button variant="ghost" size="icon" className="text-muted-foreground hover:text-destructive shrink-0" onClick={() => delColl(i)} data-testid={`collaboratore-del-${i}`}><Trash2 className="w-4 h-4" /></Button>
            </div>
          ))}
          <Button variant="outline" size="sm" onClick={addColl} data-testid="collaboratore-add"><Plus className="w-4 h-4 mr-1.5" /> Aggiungi collaboratore</Button>
          <p className="text-xs text-muted-foreground">Prefisso Italia (+39) aggiunto automaticamente se manca. Ricordati di salvare.</p>
        </div>
      </Section>

      <div className="flex justify-end">
        <Button onClick={save} disabled={saving} size="lg" data-testid="settings-save-button">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null} Salva impostazioni
        </Button>
      </div>

      <BackupCard />
    </div>
  );
}
